import { useRef, useState, useEffect } from 'react';

import useDesktopMap from '@/utils/hooks/useDesktopMap';
import useWindowSize from '@/utils/hooks/useWindowSize';

import {
  analytics,
  getExploreMapInteractionDetailWindowNav,
  getExploreMapInteractionMapPin,
  getExploreMapInteractionSidebar,
} from '@/utils/analytics';
import { getContent } from '@/utils/helper';
import {
  activateStationsInCategory,
  changeMapPosition,
  resetStation,
  selectStation,
} from '@/organisms/MapNavigation/updateMap';

import Icon from '@/atoms/Icon/Icon';
import CategorySidebar from '@/organisms/CategorySidebar/CategorySidebar';
import ExploreStationSidebar from '@/organisms/ExploreStationSidebar/ExploreStationSidebar';
import MapNavigationDesktop from '@/organisms/MapNavigation/MapNavigationDesktop';
import MapNavigationMobile from '@/organisms/MapNavigation/MapNavigationMobile';
import StationDetailSidebar from '@/organisms/StationDetailSidebar/StationDetailSidebar';

function MapModal({ open, closeModal, fields, entries, assets }) {
  const mapRef = useRef(null);
  const stationDetailRef = useRef(null);
  const mapSvgRef = useRef(null);
  const mobileMapSvgRef = useRef(null);

  const { isMobile, windowHeight, windowWidth } = useWindowSize();
  const desktopSizePosition = useDesktopMap({
    ref: mapSvgRef?.current,
    isMobile,
    windowHeight,
    windowWidth,
  });

  const [currentStation, setCurrentStation] = useState();
  const [currentCategory, setCurrentCategory] = useState();
  const [isExploreSidebarOpen, setExploreSidebarOpen] = useState(false);

  const onModalClose = () => {
    if (isMobile) {
      if (currentStation) return setCurrentStation(null);
      if (currentCategory) return setCurrentCategory(null);
    }

    return closeModal();
  };

  const closeStationSidebar = () => setCurrentStation(null);
  const exploreStationClicked = (station) => {
    setCurrentStation(station);

    analytics({
      window,
      document,
      detail: getExploreMapInteractionSidebar(null, {
        station: station?.detail?.name,
      }),
    });
  };

  const scrollStationTop = () => {
    if (stationDetailRef?.current) {
      stationDetailRef.current.scroll({ top: 0 });
    }
  };

  const goToPreviousStation = () => {
    const { prevCategoryIdx, prevStationIdx } = currentStation?.detail ?? {};
    const previousStation =
      categories[prevCategoryIdx]?.stations?.detail?.[prevStationIdx];
    setCurrentStation({
      categoryIdx: prevCategoryIdx,
      stationIdx: prevStationIdx,
      detail: previousStation,
    });

    analytics({
      window,
      document,
      detail: getExploreMapInteractionDetailWindowNav(null, {
        station: previousStation?.name,
      }),
    });
  };

  const goToNextStation = () => {
    const { nextCategoryIdx, nextStationIdx } = currentStation?.detail ?? {};
    const nextStation =
      categories[nextCategoryIdx]?.stations?.detail?.[nextStationIdx];
    setCurrentStation({
      categoryIdx: nextCategoryIdx,
      stationIdx: nextStationIdx,
      detail: nextStation,
    });

    analytics({
      window,
      document,
      detail: getExploreMapInteractionDetailWindowNav(null, {
        station: nextStation?.name,
      }),
    });
  };

  const onStationMapClick = (categoryId, stationId) => {
    const category = categories.find((c) => c.id === categoryId);
    const stationIdx = category.stations.detail.findIndex(
      (s) => s.id === stationId
    );

    const station = category?.stations?.detail[stationIdx];
    setCurrentStation(() => ({
      categoryIdx: category?.categoryIdx,
      detail: station,
      stationIdx: stationIdx,
    }));

    analytics({
      window,
      document,
      detail: getExploreMapInteractionMapPin(null, {
        station: station?.name,
      }),
    });
  };

  const closeCategorySidebar = () => setCurrentCategory(null);

  const categoryStationClicked = (station) => {
    setCurrentStation(station);

    analytics({
      window,
      document,
      detail: getExploreMapInteractionDetailWindowNav(null, {
        station: station?.detail?.name,
      }),
    });
  };

  const goToPreviousCategory = () => {
    const { prevCategoryIdx } = currentCategory?.detail ?? {};
    const previousCategory = categories[prevCategoryIdx];
    setCurrentCategory({
      categoryIdx: prevCategoryIdx,
      detail: previousCategory,
    });

    analytics({
      window,
      document,
      detail: getExploreMapInteractionDetailWindowNav(null, {
        category: previousCategory?.name,
      }),
    });
  };

  const goToNextCategory = () => {
    const { nextCategoryIdx } = currentCategory?.detail ?? {};
    const nextCategory = categories[nextCategoryIdx];
    setCurrentCategory({
      categoryIdx: nextCategoryIdx,
      detail: nextCategory,
    });

    analytics({
      window,
      document,
      detail: getExploreMapInteractionDetailWindowNav(null, {
        category: nextCategory?.name,
      }),
    });
  };

  const onCategoryPinClick = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    setCurrentCategory(() => ({
      categoryIdx: category?.categoryIdx,
      detail: category,
    }));

    analytics({
      window,
      document,
      detail: getExploreMapInteractionMapPin(null, {
        category: category?.name,
      }),
    });
  };

  useEffect(() => {
    if (!!currentStation && currentCategory) {
      setCurrentStation(null);
    }

    const svgRef = isMobile ? mobileMapSvgRef : mapSvgRef;
    if (svgRef) {
      if (!currentStation) {
        resetStation(svgRef?.current, isMobile);
      }
      const categoryId =
        currentCategory?.detail?.id ?? currentStation?.detail?.categoryId;
      const stationId = currentStation?.detail?.id;
      activateStationsInCategory(svgRef?.current, {
        categoryId,
        stationId: currentCategory ? null : stationId,
      });
      changeMapPosition(svgRef?.current, {
        categoryId,
        stationId,
        isMobile: isMobile,
        desktopSizePosition,
      });
    }
  }, [currentCategory]);

  useEffect(() => {
    const svgRef = isMobile ? mobileMapSvgRef : mapSvgRef;
    if (svgRef?.current) {
      const station = svgRef.current.querySelector(
        `#${currentStation?.detail?.id}`
      );

      if (currentStation) {
        scrollStationTop();
        selectStation(svgRef.current, station, isMobile);
      } else if (!currentCategory) {
        resetStation(svgRef.current, isMobile);
      }
    }
    if (!!currentCategory && currentStation) {
      setCurrentCategory(null);
    }

    changeMapPosition(svgRef?.current, {
      categoryId:
        currentCategory?.detail?.id ?? currentStation?.detail?.categoryId,
      stationId: currentStation?.detail?.id,
      isMobile: isMobile,
      desktopSizePosition,
    });
  }, [mapSvgRef, mobileMapSvgRef, currentStation]);

  useEffect(() => {
    return isMobile ? updateMobileMapSize() : updateDesktopMapSize();

    function updateMobileMapSize() {
      if (!mobileMapSvgRef?.current) return;
      mobileMapSvgRef.current.style.width = `${windowWidth}px`;
    }

    function updateDesktopMapSize() {
      mapSvgRef.current.style.height = `${windowHeight}px`;
      mapSvgRef.current.style.width = `${windowWidth}px`;
    }
  }, [mapRef, mapSvgRef, mobileMapSvgRef, windowHeight, windowWidth]);

  let categories = [];
  const categorySize = fields?.category?.length;
  if (categorySize > 0) {
    // category without station
    categories = fields.category.map((c, categoryIdx) => {
      const { fields: category } = getContent(c?.sys?.id, entries);
      const minimapImage = category?.minimapImage?.[0]?.url;
      const prevCategoryIdx =
        categoryIdx >= 1 ? categoryIdx - 1 : categorySize - 1;
      const nextCategoryIdx =
        categoryIdx < categorySize - 1 ? categoryIdx + 1 : 0;
      return {
        ...category,
        minimapImage,
        categoryIdx,
        prevCategoryIdx,
        nextCategoryIdx,
        rawStations: category?.stations,
      };
    });
    // add station detail to category
    categories = categories.map(({ rawStations, ...category }, categoryIdx) => {
      const totalStation = rawStations?.length;
      if (!totalStation)
        return {
          ...category,
          stations: { detail: [] },
        };

      const { prevCategoryIdx, nextCategoryIdx } = category;

      const stations = rawStations.map((s, stationIdx) => {
        const { fields: station } = getContent(s?.sys?.id, entries);

        const hasNextStation = stationIdx < totalStation - 1;
        const nextStationIdx = hasNextStation ? stationIdx + 1 : 0;

        const hasPrevStation = stationIdx >= 1;
        const prevStationIdx = hasPrevStation
          ? stationIdx - 1
          : categories[prevCategoryIdx]?.rawStations?.length - 1;

        return {
          ...station,
          categoryId: category.id,
          // for bottom next prev navigation
          nextStationIdx,
          prevStationIdx,
          nextCategoryIdx: hasNextStation ? categoryIdx : nextCategoryIdx,
          prevCategoryIdx: hasPrevStation ? categoryIdx : prevCategoryIdx,
        };
      });

      return {
        ...category,
        stations: {
          detail: stations,
          categoryIdx,
        },
      };
    });
  }

  const categoryList =
    categories?.length > 0 ? categories.map((c) => c.name) : [];

  const hasSidebarOpen =
    Boolean(currentCategory) || Boolean(currentStation) || isExploreSidebarOpen;

  return (
    <div
      ref={mapRef}
      className={`aaco-modal fixed top-0 left-0 w-screen h-full md:h-screen md:max-h-screen ${
        hasSidebarOpen ? 'overflow-y-auto' : 'overflow-y-hidden'
      } overflow-x-hidden md:overflow-y-none text-white z-[100]
      transition-all duration-300 ${
        open ? 'visible opacity-100' : 'invisible opacity-0'
      }`}
    >
      <div
        className="absolute top-0 right-0 cursor-pointer z-20 w-[106px] h-[106px] flex items-center justify-center"
        onClick={onModalClose}
      >
        <Icon name="close" className="text-acacia" />
      </div>

      <div className="md:hidden absolute top-0 left-0 h-screen w-screen overflow-x-hidden">
        {isMobile && (
          <MapNavigationMobile
            backgroundSrc={fields?.mapImageMobile?.[0]?.url}
            svgRef={mobileMapSvgRef}
            onCategoryPinClick={onCategoryPinClick}
            onStationMapClick={onStationMapClick}
            closeStationSidebar={closeStationSidebar}
          />
        )}
      </div>

      <div className="aaco-map-preview__image hidden md:block absolute top-0 left-0 h-full w-full z-10">
        <div className="hidden md:flex items-end w-fit absolute bottom-[95px] left-0 pl-8 z-20">
          <img
            className="w-[91px] h-[91px]"
            src={fields?.minimapImageDesktop?.[0]?.url}
            alt="minimap"
          />
        </div>
        {!isMobile && (
          <MapNavigationDesktop
            backgroundSrc={fields?.mapImageDesktop?.[0]?.url}
            svgRef={mapSvgRef}
            onCategoryPinClick={onCategoryPinClick}
            onStationMapClick={onStationMapClick}
            closeStationSidebar={closeStationSidebar}
          />
        )}
      </div>
      <div className="w-screen md:h-screen flex flex-col md:flex-row justify-end md:justify-start">
        <div className="aaco-navigation__page flex flex-col md:w-full md:flex-row md:justify-between">
          <>
            <div className="md:hidden aaco-navigation__page__minimap">
              <img
                className={`w-[91px] h-[91px] ${
                  (currentCategory?.detail?.minimapImage ||
                    currentStation?.detail?.minimapImage) &&
                  (Boolean(currentCategory) || Boolean(currentStation))
                    ? ''
                    : 'hidden'
                }`}
                src={
                  currentCategory?.detail?.minimapImage ??
                  currentStation?.detail?.minimapImage
                }
                alt="minimap"
              />
            </div>
            <ExploreStationSidebar
              isMobile={isMobile}
              title={fields?.stationSidebarTitle}
              categories={categories}
              onStationClick={exploreStationClicked}
              sidebarOpen={Boolean(currentCategory) || Boolean(currentStation)}
              isOpen={isExploreSidebarOpen}
              setSidebarOpen={setExploreSidebarOpen}
            />
          </>

          <CategorySidebar
            isMobile={isMobile}
            prevCategory={
              categoryList?.[currentCategory?.detail?.prevCategoryIdx]
            }
            nextCategory={
              categoryList?.[currentCategory?.detail?.nextCategoryIdx]
            }
            category={currentCategory?.detail}
            goToNextCategory={goToNextCategory}
            goToPreviousCategory={goToPreviousCategory}
            closeCategorySidebar={closeCategorySidebar}
            categoryIdx={currentCategory?.categoryIdx}
            onStationClick={categoryStationClicked}
          />
          <StationDetailSidebar
            isMobile={isMobile}
            station={currentStation?.detail}
            stationRef={stationDetailRef}
            assets={assets}
            entries={entries}
            goToPreviousStation={goToPreviousStation}
            goToNextStation={goToNextStation}
            closeStationSidebar={closeStationSidebar}
          />
        </div>
      </div>
    </div>
  );
}

export default MapModal;
